import { computed, ref, watchEffect } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useFlag } from "./useFlag";

// TODO: populate with MSP UUIDs that should see Connections V2 ahead of the global rollout.
export const WHITELIST_MSP_UUIDS: string[] = [
  // DEV MSPS
  "c339973b-a358-4eca-ad9f-439469d62e78",
  // STAGING MSPS
  "12084856-e016-43f3-a0f4-3dde967ac138",
  // PROD MSPS
  "640963cd-ca86-4bb6-a471-4b83f291a64a",
  "6330ab70-48c8-4c07-b702-04032ff30088",
  "8468824e-2e1f-4101-8307-8d6fca1d473b",
  "c91a63fc-8012-4993-9df0-33a79e9ff9d1",
];

export const useIsConnectionsV2Available = () => {
  const { isFeatureAvailable } = useFlag("CONNECTIONS_V2");
  const authStore = useAuthenticationStore();
  const organisationsStore = useOrganisationsStore();

  const selectedMsp = computed<{ uuid?: string } | null>(
    () => authStore.selectedMsp ?? null,
  );

  const selectedAccount = computed<{
    uuid?: string;
    type_name?: string;
  } | null>(() => authStore.selectedAccount ?? null);

  // Customer-direct logins have no selectedMsp and the JWT organisation claim
  // omits parent_uuid, so resolve the parent MSP via /v1/idp/orgs/{uuid}.
  const resolvedParentUuid = ref<string | null>(null);

  watchEffect(async () => {
    resolvedParentUuid.value = null;

    if (isFeatureAvailable.value) return;
    if (selectedMsp.value?.uuid) return;

    const account = selectedAccount.value;
    if (account?.type_name !== "Customer" || !account?.uuid) return;

    try {
      const org = (await organisationsStore.getOrgWithAncestors({
        uuid: account.uuid,
      })) as { parent_uuid?: string | null } | null;
      resolvedParentUuid.value = org?.parent_uuid ?? null;
    } catch {
      resolvedParentUuid.value = null;
    }
  });

  const isConnectionsV2Available = computed(() => {
    if (isFeatureAvailable.value) return true;
    const candidateUuid = selectedMsp.value?.uuid ?? resolvedParentUuid.value;
    if (!candidateUuid) return false;
    return WHITELIST_MSP_UUIDS.includes(candidateUuid);
  });

  return { isConnectionsV2Available };
};
